/* assets/ddi-offer-cart.js */

(function () {
  if (window.__ddi_offer_cart_init) return;
  window.__ddi_offer_cart_init = true;

  const ACTIVE_CLASS = "ddi-active";

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const toIdSet = (csv) => {
    if (!csv) return new Set();
    return new Set(
      String(csv)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => Number(s))
        .filter((n) => Number.isFinite(n))
    );
  };

  const setText = (el, next) => {
    if (!el) return;
    const val = (next == null ? "" : String(next)).trim();
    const cur = (el.textContent || "").trim();
    if (cur === val) return;
    el.textContent = val;
  };

  const fetchCart = async () => {
    const res = await fetch("/cart.js", { credentials: "same-origin", cache: "no-store" });
    if (!res.ok) throw new Error("cart.js failed");
    return res.json();
  };

  /* --- API-based collection ID fetching (bypasses Liquid 20-product cap) --- */
  let cachedLampIds = null;
  let cachedHomeIds = null;
  let lampIdsFetchPromise = null;
  let homeIdsFetchPromise = null;

  const fetchCollectionIds = (handle) =>
    fetch(`/collections/${handle}/products.json?limit=250&fields=id`, {
      credentials: "same-origin",
      cache: "no-store",
    })
      .then((r) => r.json())
      .then((d) => new Set((d.products || []).map((p) => Number(p.id))))
      .catch(() => null);

  const getLampIds = (fallbackCsv) => {
    if (cachedLampIds) return Promise.resolve(cachedLampIds);
    if (!lampIdsFetchPromise) {
      lampIdsFetchPromise = fetchCollectionIds("lighting-excl-bundles").then((ids) => {
        if (ids && ids.size > 0) cachedLampIds = ids;
        lampIdsFetchPromise = null;
        return cachedLampIds || toIdSet(fallbackCsv);
      });
    }
    return lampIdsFetchPromise;
  };

  const getHomeIds = (fallbackCsv) => {
    if (cachedHomeIds) return Promise.resolve(cachedHomeIds);
    if (!homeIdsFetchPromise) {
      homeIdsFetchPromise = fetchCollectionIds("homewares-excl-bundles").then((ids) => {
        if (ids && ids.size > 0) cachedHomeIds = ids;
        homeIdsFetchPromise = null;
        return cachedHomeIds || toIdSet(fallbackCsv);
      });
    }
    return homeIdsFetchPromise;
  };
  /* ----------------------------------------------------------------------- */

  const cartSignature = (cart) => {
    const items = cart && cart.items ? cart.items : [];
    return items
      .map((it) => `${it.product_id}:${it.quantity}`)
      .sort()
      .join("|");
  };

  const getCounts = (cart, lampIds, homeIds) => {
    let lamps = 0;
    let home = 0;

    const items = cart && cart.items ? cart.items : [];
    for (const it of items) {
      const pid = Number(it.product_id);
      const qty = Number(it.quantity) || 0;
      if (lampIds.has(pid)) lamps += qty;
      if (homeIds.has(pid)) home += qty;
    }
    return { lamps, home };
  };

  // Tier keys used by markup
  // Lamps: 0-1 / 2 / 3 / 4
  // Home:  0-1 / 2 / 4 / 6
  const lampsTierKey = (count) => {
    if (count >= 4) return "4";
    if (count === 3) return "3";
    if (count === 2) return "2";
    return "0-1";
  };

  const homeTierKey = (count) => {
    if (count >= 6) return "6";
    if (count >= 4) return "4";
    if (count >= 2) return "2";
    return "0-1";
  };

  const nextMessageLamps = (count) => {
    if (count >= 4) return "Max unlocked";
    if (count === 3) return "Add 1 for 25% off your total set";
    if (count === 2) return "Add 1 for 20% off your total set";
    if (count === 1) return "Add 1 for 15% off your total set";
    return "Add 2 for 15% off your total set";
  };

  const nextMessageHome = (count) => {
    if (count >= 6) return "Max unlocked";
    if (count >= 4) return "Add 2 for 30% off your total set";
    if (count >= 2) return "Add 2 for 20% off your total set";
    if (count === 1) return "Add 1 for 10% off your total set";
    return "Add 2 for 10% off your total set";
  };

  const lampsStatusText = (count) => {
    if (count >= 4) return "25% off applied";
    if (count === 3) return "20% off applied";
    if (count >= 2) return "15% off applied";
    return "";
  };

  const homeStatusText = (count) => {
    if (count >= 6) return "30% off applied";
    if (count >= 4) return "20% off applied";
    if (count >= 2) return "10% off applied";
    return "";
  };

  /* Lamp cross-category deal addon text — dynamic based on cart state */
  const addonText = (lamps, home) => {
    if (lamps >= 1 && home >= 2) return "Lamp deal applied \u2014 2 homewares at 30% off";
    if (lamps >= 1 && home === 1) return "Lamp deal active \u2014 add 1 more homeware for 30% off";
    if (lamps >= 1) return "Lamp deal active \u2014 add homewares for 30% off";
    return "Buy 1 lamp, get up to 2 homewares at 30% off";
  };

  const updateBannerDots = (block, lamps, home) => {
    // Lamps track: 3 dots for tiers 15% / 20% / 25%
    const lampsTrack = block.querySelector('[data-ddi-track="lamps"]');
    if (lampsTrack) {
      const on = lamps >= 4 ? 3 : lamps >= 3 ? 2 : lamps >= 2 ? 1 : 0;
      if (on > 0) lampsTrack.classList.add("is-on");
      else lampsTrack.classList.remove("is-on");
      lampsTrack.querySelectorAll(".ddi-dot").forEach((dot, i) => {
        if (i < on) dot.classList.add("is-on");
        else dot.classList.remove("is-on");
      });
    }

    // Homewares track: 3 dots for tiers 10% / 20% / 30%
    const homeTrack = block.querySelector('[data-ddi-track="homewares"]');
    if (homeTrack) {
      const on = home >= 6 ? 3 : home >= 4 ? 2 : home >= 2 ? 1 : 0;
      if (on > 0) homeTrack.classList.add("is-on");
      else homeTrack.classList.remove("is-on");
      homeTrack.querySelectorAll(".ddi-dot").forEach((dot, i) => {
        if (i < on) dot.classList.add("is-on");
        else dot.classList.remove("is-on");
      });
    }

    // Addon track: 2 dots, representing 2 homeware bonus tiers (2+ / 4+)
    const addonTrack = block.querySelector('[data-ddi-track="addon"]');
    if (addonTrack) {
      const on = home >= 4 ? 2 : home >= 2 ? 1 : 0;
      if (on > 0) addonTrack.classList.add("is-on");
      else addonTrack.classList.remove("is-on");
      addonTrack.querySelectorAll(".ddi-dot").forEach((dot, i) => {
        if (i < on) dot.classList.add("is-on");
        else dot.classList.remove("is-on");
      });
    }
  };


  const clearActive = (root, scope) => {
    if (!root) return;

    root
      .querySelectorAll(`[data-ddi-scope="${scope}"][data-ddi-tier].${ACTIVE_CLASS}`)
      .forEach((el) => el.classList.remove(ACTIVE_CLASS));

    root
      .querySelectorAll(
        `[data-ddi-scope="${scope}"][data-ddi-tier] [data-ddi-cell="discount"].${ACTIVE_CLASS}`
      )
      .forEach((el) => el.classList.remove(ACTIVE_CLASS));
  };

  const setActiveTier = (root, scope, tierKey) => {
    if (!root) return;

    clearActive(root, scope);

    const row = root.querySelector(`[data-ddi-scope="${scope}"][data-ddi-tier="${tierKey}"]`);
    if (!row) return;

    row.classList.add(ACTIVE_CLASS);
    const discountCell = row.querySelector(`[data-ddi-cell="discount"]`);
    if (discountCell) discountCell.classList.add(ACTIVE_CLASS);
  };

  const updateCounts = (block, lamps, home) => {
    // PDP uses [data-ddi-counts] (single string)
    const combined = block.querySelector("[data-ddi-counts]");
    if (combined) {
      setText(combined, `Lighting: ${lamps}  Homewares: ${home}`);
    }

    // Cart drawer version (spans) if present
    const cL = block.querySelector("[data-ddi-count-lamps]");
    const cH = block.querySelector("[data-ddi-count-home]");
    if (cL) setText(cL, String(lamps));
    if (cH) setText(cH, String(home));
  };

  const updateBuildCta = (block, lamps, home) => {
    // Find the CTA scoped to the same form as this block
    const form = block.closest("form") || document;
    const cta = form.querySelector("[data-ddi-build-cta]");
    if (!cta) return;

    const labelEl = cta.querySelector("[data-ddi-cta-label]");
    const lampsLink = cta.querySelector("[data-ddi-cta-lamps]");
    const homeLink = cta.querySelector("[data-ddi-cta-home]");
    const sep = cta.querySelector("[data-ddi-cta-sep]");

    const lampsMaxed = lamps >= 4;
    const homeMaxed = home >= 6;

    let label = "";
    let showLamps = false;
    let showHome = false;

    if (lampsMaxed && homeMaxed) {
      label = "Max savings unlocked \u2728";
    } else if (!lampsMaxed) {
      if (lamps === 0) {
        label = "Add 2+ lamps to start saving";
      } else if (lamps === 1) {
        label = "Add 1 more lamp to unlock 15% off";
      } else if (lamps === 2) {
        label = "Add 1 more lamp for 20% off";
      } else {
        label = "Add 1 more lamp for 25% off";
      }
      showLamps = true;
      showHome = !homeMaxed;
    } else {
      // lamps maxed, home not
      if (home === 0) {
        label = "Add 2 homewares for 10% off";
      } else if (home === 1) {
        label = "Add 1 more homeware for 10% off";
      } else if (home === 2 || home === 3) {
        label = "Add " + (4 - home) + " more homewares for 20% off";
      } else {
        label = "Add " + (6 - home) + " more homewares for 30% off";
      }
      showLamps = false;
      showHome = true;
    }

    if (labelEl) setText(labelEl, label);
    if (lampsLink) lampsLink.style.display = showLamps ? "" : "none";
    if (homeLink) homeLink.style.display = showHome ? "" : "none";
    if (sep) sep.style.display = (showLamps && showHome) ? "" : "none";
  };

  const updateBlock = (block, cart, lampIds, homeIds, sig) => {
    if (!block) return;

    const { lamps, home } = getCounts(cart, lampIds, homeIds);

    updateCounts(block, lamps, home);

    setText(block.querySelector("[data-ddi-next-lamps]"), nextMessageLamps(lamps));
    setText(block.querySelector("[data-ddi-next-home]"), nextMessageHome(home));
    setText(block.querySelector("[data-ddi-status-lamps]"), lampsStatusText(lamps));
    setText(block.querySelector("[data-ddi-status-home]"), homeStatusText(home));

    const addon = block.querySelector("[data-ddi-addon]");
    if (addon) setText(addon, addonText(lamps, home));

    setActiveTier(block, "lamps", lampsTierKey(lamps));
    setActiveTier(block, "home", homeTierKey(home));
    updateBannerDots(block, lamps, home);

    // Update collapsible summary status text
    const details = block.closest("details.ddi-collapsible");
    if (details) {
      const statusEl = details.querySelector("[data-ddi-summary-status]");
      if (statusEl) {
        let msg = "";
        if (lamps >= 2 || home >= 2) {
          const parts = [];
          if (lamps >= 4) parts.push("Lighting 25% off");
          else if (lamps === 3) parts.push("Lighting 20% off");
          else if (lamps >= 2) parts.push("Lighting 15% off");
          if (home >= 6) parts.push("Homewares 30% off");
          else if (home >= 4) parts.push("Homewares 20% off");
          else if (home >= 2) parts.push("Homewares 10% off");
          msg = parts.join(" \u00B7 ");
        } else if (lamps === 1) {
          msg = "Add 1 more lamp to unlock 15% off";
        } else {
          msg = "Add 2+ lamps to unlock savings";
        }
        setText(statusEl, msg);
      }
    }

    // Update keep-building CTA with tier-aware nudge
    updateBuildCta(block, lamps, home);

    block.setAttribute("data-ddi-last-sig", sig);
  };

  let lastCartSig = "";

  const paintAllBlocks = async (force = false) => {
    const blocks = document.querySelectorAll("[data-ddi-offer]");
    if (!blocks.length) return;

    // Fetch cart and ID sets in parallel
    const firstBlock = blocks[0];
    const fallbackLampCsv = firstBlock.getAttribute("data-lamp-ids") || "";
    const fallbackHomeCsv = firstBlock.getAttribute("data-home-ids") || "";

    const [cart, lampIds, homeIds] = await Promise.all([
      fetchCart(),
      getLampIds(fallbackLampCsv),
      getHomeIds(fallbackHomeCsv),
    ]);

    const sig = cartSignature(cart);

    const cartChanged = sig !== lastCartSig;
    if (cartChanged) lastCartSig = sig;

    blocks.forEach((block) => {
      const blockSig = block.getAttribute("data-ddi-last-sig") || "";
      if (force || cartChanged || blockSig !== sig) {
        updateBlock(block, cart, lampIds, homeIds, sig);
      }
    });
  };

  const runUpdate = async (force = false) => {
    try {
      await paintAllBlocks(force);
    } catch (e) {
      // silent
    }
  };

  const scheduleUpdate = debounce(() => runUpdate(false), 60);

  // Initial
  scheduleUpdate();

  // Theme events (if fired)
  window.addEventListener("cart:updated", scheduleUpdate);
  window.addEventListener("cart:change", scheduleUpdate);
  window.addEventListener("cart:refresh", scheduleUpdate);
  window.addEventListener("theme:cart:updated", scheduleUpdate);
  window.addEventListener("ajaxCart:updated", scheduleUpdate);

  // Observe common containers so we repaint immediately after drawer swaps
  const observeAndForce = (node) => {
    if (!node) return;
    const obs = new MutationObserver(() => {
      runUpdate(true);
      requestAnimationFrame(() => runUpdate(true));
    });
    obs.observe(node, { childList: true, subtree: true });
  };

  observeAndForce(document.getElementById("site-cart-sidebar"));
  observeAndForce(document.getElementById("AjaxCartForm"));

  // Also repaint after clicks on qty controls / remove links as a fallback
  document.addEventListener(
    "click",
    (e) => {
      const hit = e.target.closest(
        '.qty-button, a[href*="/cart/change"], button[name="plus"], button[name="minus"], [data-js-cart-remove]'
      );
      if (!hit) return;
      setTimeout(() => runUpdate(true), 120);
      setTimeout(() => runUpdate(true), 350);
    },
    { passive: true }
  );
})();
